import { WheelOption } from "@/data/wheels";
import { Alignment } from "@/lib/rng";
import { SpinResult } from "@/components/ResultsSummary";
import { ChaosGameState } from "@/lib/chaosEngine";
import type {
  AbilityStep,
  CrownStage,
  FightStep,
  GamePhase,
} from "@/hooks/useGameState";

// Pre-namespacing single device-wide slot. Retained ONLY so an in-progress run
// saved before per-identity slots existed can be migrated across once, the first
// time a player loads the namespaced build (see migrateLegacyRun).
const KEY = "heroOrAVillain:activeRun";
// Demo runs persist under a completely separate key so a "just for fun" demo
// can never read, overwrite, or interfere with a real active run.
export const DEMO_KEY = "heroOrAVillain:demoRun";
// "Vs friend" matches also run on their own slot, so starting/playing a match
// never touches the player's in-progress solo run.
export const MATCH_KEY = "heroOrAVillain:matchRun";

// Not-signed-in play shares one guest slot; each signed-in account gets its own
// `...:run:<uid>` slot. This per-identity namespacing is what stops two accounts
// on the SAME device from reading (or overwriting) each other's solo run.
export const GUEST_RUN_KEY = "heroOrAVillain:run:guest";
export function soloRunKey(userId: string | null | undefined): string {
  return userId ? `heroOrAVillain:run:${userId}` : GUEST_RUN_KEY;
}

const VERSION = 3;

// Full snapshot of an in-progress run. Anything not listed here (spin
// animation tokens, transient chaos-overlay stages, etc.) resets cleanly on
// reload, so it's deliberately left out.
export interface ActiveRunState {
  version: number;
  updatedAt: number;
  phase: GamePhase;
  alignment: Alignment | null;
  sidekickOf: Alignment | null;
  currentIndex: number;
  results: SpinResult[];
  pendingOption: WheelOption | null;
  showResult: boolean;
  abilityStep: AbilityStep;
  abilityCount: number;
  abilitiesCompleted: number;
  currentAbilityCategory: string | null;
  fightStep: FightStep;
  fightResults: SpinResult[];
  hasCrown: boolean;
  crownStage: CrownStage;
  crownBonusOption: WheelOption | null;
  chaosResults: SpinResult[];
  chaosGameState: ChaosGameState;
  spinsSinceLastChaos: number;
  // When the run first reached the results screen (ms epoch). Drives the
  // reroll cooldown so it survives refresh and resumes from the real start
  // instead of resetting on every results-screen mount. Null until completion.
  cooldownStartedAt: number | null;
  // The leaderboard id once this run has been submitted. Persisted so the run
  // survives navigation after submitting (instead of being cleared) and so the
  // results screen knows not to offer a second submission. Optional for
  // backward-compat with runs saved before this field existed.
  submittedScoreId?: string | null;
  // How many paid single-wheel rerolls have been spent on this run (capped at
  // MAX_SINGLE_WHEEL_REROLLS). Optional for backward-compat — absent means 0.
  // NOTE: deliberately NOT version-bumped, so existing saved runs stay valid.
  wheelRerollsUsed?: number;
}

// A freshly-initialized run looks identical to "no run at all", so don't
// surface a restore toast for it.
export function hasRunProgress(state: ActiveRunState): boolean {
  return (
    state.phase !== "alignment" ||
    state.alignment !== null ||
    state.results.length > 0
  );
}

export function saveActiveRun(state: ActiveRunState, key: string = KEY): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(state));
}

// Structural guard for any persisted run, local OR remote. A run must match the
// current version AND carry the nested shape the engine dereferences (notably
// chaosGameState.wheels / .chaosFlags). Anything older or partial — a run saved
// before the chaos system, or a stale cross-device copy — is rejected so the
// app starts a clean run instead of crashing mid-spin on undefined fields.
export function isValidRun(state: unknown): state is ActiveRunState {
  if (!state || typeof state !== "object") return false;
  const s = state as Record<string, unknown>;
  if (s.version !== VERSION) return false;
  if (!Array.isArray(s.results) || !Array.isArray(s.chaosResults)) return false;
  const cgs = s.chaosGameState as Record<string, unknown> | null | undefined;
  if (!cgs || typeof cgs !== "object") return false;
  if (!cgs.wheels || typeof cgs.wheels !== "object") return false;
  if (!cgs.chaosFlags || typeof cgs.chaosFlags !== "object") return false;
  if (!Array.isArray(cgs.abilities)) return false;
  return true;
}

export function loadActiveRun(key: string = KEY): ActiveRunState | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!isValidRun(parsed)) {
      // Purge incompatible/corrupt state so the auto-save effect can't keep
      // re-stamping it with the current version and resurrecting the crash.
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
}

export function clearActiveRun(key: string = KEY): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(key);
}

// One-time upgrade from the old device-wide single-slot key. If a legacy run
// exists and the target identity slot is empty, move it across so an in-progress
// run isn't lost the first time a player loads the namespaced build. The legacy
// key is always consumed (deleted) so it can only ever migrate once, to whoever
// loads first; an already-populated identity slot is never clobbered.
export function migrateLegacyRun(targetKey: string): void {
  if (typeof window === "undefined") return;
  const legacy = window.localStorage.getItem(KEY);
  if (legacy === null) return;
  if (window.localStorage.getItem(targetKey) === null) {
    window.localStorage.setItem(targetKey, legacy);
  }
  window.localStorage.removeItem(KEY);
}

// Carries a not-signed-in player's in-progress run into their account the first
// time they register. Deliberately only fills an EMPTY account slot, so logging
// into an existing account never has its own run replaced by a guest's (option
// A: the existing account always wins). Returns the carried run, or null if
// there was nothing to carry / the account already had a run.
export function migrateGuestRunToAccount(uid: string): ActiveRunState | null {
  if (typeof window === "undefined") return null;
  const accountKey = soloRunKey(uid);
  const existing = loadActiveRun(accountKey);
  if (existing && hasRunProgress(existing)) return null;
  const guestRun = loadActiveRun(GUEST_RUN_KEY);
  if (!guestRun || !hasRunProgress(guestRun)) return null;
  saveActiveRun(guestRun, accountKey);
  clearActiveRun(GUEST_RUN_KEY);
  return guestRun;
}
